import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { finalize, take, tap } from 'rxjs/operators';
import { ProjectQuery } from 'src/app/projects/state/project.query';
import { GeometricFormService } from '../../state/geometric-form.service';
import { GeometricFormType } from '../../state/geometric-form.type';
import { Vertex } from '../../vertexes/state/vertex.model';
import { Building } from './building.model';
import { BuildingStore } from './building.store';

@Injectable({ providedIn: 'root' })
export class BuildingService extends GeometricFormService<Building>{

  constructor(
    private buildingStore: BuildingStore,
    private projectQuery: ProjectQuery,
    private http: HttpClient
  ) {
    super(buildingStore);
  }

  get() {
    this.buildingStore.setLoading(true);
    return this.http.get<Building[]>(`/api/project/${this.projectQuery.getActiveId()}/building/`)
      .pipe(tap(entities => {
        this.buildingStore.set(entities);
      }),
        finalize(() => this.buildingStore.setLoading(false))
      );
  }

  add(building: Building) {
    return this.http.post<Building>(`/api/project/${this.projectQuery.getActiveId()}/building/`, building)
      .pipe(tap(b => {
        this.buildingStore.add(b);
      }));
  }

  update(id: number, building: Partial<Building>) {
    return this.http.put<Building>(`/api/project/${this.projectQuery.getActiveId()}/building/${id}`, building)
      .pipe(tap(b => {
        this.buildingStore.update(id, b);
      }));
  }

  remove(id: number) {
    return this.http.delete(`/api/project/${this.projectQuery.getActiveId()}/building/${id}`)
      .pipe(tap(() => {
        this.buildingStore.remove(id);
      }));
  }

  getFull(id: number) {
    this.buildingStore.setLoading(true);
    return this.http.get<Building>(`/api/project/${this.projectQuery.getActiveId()}/building/${id}`)
      .pipe(tap(building => {
        this.buildingStore.update(id, building);
      }),
        finalize(() => this.buildingStore.setLoading(false))
      );
  }

  getType(): GeometricFormType {
    return 'mitra-building';
  }

  connect(building: Building, vertex: Vertex) {
    return this.http.post<any>(
      `/api/project/${this.projectQuery.getActiveId()}/building/${building.id}/connection`,
      { vertex_pk: vertex.id }
    )
      .pipe(tap(res => {
        this.buildingStore.update(building.id, {
          vertex: vertex.id,
          vertex_latitude: vertex.latitude,
          vertex_longitude: vertex.longitude
        });
        this.getFull(building.id).pipe(take(1)).subscribe();
      }));
  }

  filter(legends: string[]) {

    if (!legends.length) {
      this.buildingStore.update(null, { hidder: false });
      return;
    }

    this.buildingStore.update(null, ({ map_legend }) => {
      return {
        hidder: legends.indexOf(map_legend) < 0
      };
    });
  }

  move(id: number, latitude: number, longitude: number) {
    return this.http.put(
      `/api/project/${this.projectQuery.getActiveId()}/building/${id}/move`,
      { latitude, longitude }
    )
      .pipe(tap(res => {
        this.buildingStore.update(id, { latitude, longitude });
      }));
  }

  updateVertex(vertexId: number, latitude: number, longitude: number): void {
    this.buildingStore.update(
      ({ vertex }) => vertex === vertexId,
      {
        vertex_latitude: latitude,
        vertex_longitude: longitude
      }
    );
  }

  removeVertexConnections(vertexId: number): void {
    this.buildingStore.update(
      ({ vertex }) => vertex === vertexId,
      {
        vertex: null,
        vertex_latitude: null,
        vertex_longitude: null
      }
    );
  }

}
